import React, { useContext, useState } from 'react';

import { CompanyContext } from '../../../../../context/CompanyContext';
import {
  CreateStockLocationRoleAssignmentResponse,
  CreateStockLocationRoleAssignmentVariables,
  StockLocationRoleAssignmentMutation,
} from '../../../../../graphql/stockLocationRoleAssignment.graphql';
import { StockLocationRoleAssignmentContext } from '../../../../../context/StockLocationRoleAssignmentContext';
import { UserContext } from '../../../../../context/UserContext';
import { ActionModal } from '../../../../components/Modal';
import { DropdownSelect, DropdownSearchSelect } from '../../../../components/Dropdown';
import { StockLocationContext } from '../../../../../context/StockLocationContext';
import { StockLocation } from '../../../../../types/stock_location/stockLocation';
import { CancelButton, CreateButton } from '../../../../components/Button';
import { t } from '../../../../../types/translation/Translator';
import { TranslationKey } from '../../../../../types/translation/TranslationKey';
import { useToast } from '../../../../../providers/ToastProvider';
import { ToastType } from '../../../../components/Toast';
import {
  StockLocationRole,
  StockLocationRoleAssignment,
  StockLocationRoleAssignmentInput,
  StockLocationRoleAssignmentUtil,
} from '../../../../../types/stock_location_role_assignment';
import { User } from '../../../../../types/user';
import useMutation from '../../../../../hooks/useMutation';

interface CreateStockLocationRoleForUserModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  user: User;
  userStockLocationRoles: Map<string, StockLocationRoleAssignment>;
}

export default function CreateStockLocationRoleForUserModal({
  open,
  setOpen,
  user,
  userStockLocationRoles,
}: CreateStockLocationRoleForUserModalProps) {
  const { showToast } = useToast();
  const { currentCompany } = useContext(CompanyContext);
  const { stockLocationRoles, setStockLocationRoles } = useContext(StockLocationRoleAssignmentContext);
  const { companyUsers } = useContext(UserContext);
  const { stockLocations } = useContext(StockLocationContext);

  const [input, setInput] = useState(
    new StockLocationRoleAssignmentInput({
      companyId: currentCompany.id,
      userId: user.id,
      email: user.email,
      role: StockLocationRole.STOCK_LOCATION_VIEWER,
    }),
  );

  const [create, { loading }] = useMutation<
    CreateStockLocationRoleAssignmentResponse,
    CreateStockLocationRoleAssignmentVariables
  >(StockLocationRoleAssignmentMutation.create, {
    onCompleted: res => {
      const role = res.createStockLocationRoleAssignment[0];
      const existingRoles = stockLocationRoles.get(role.stockLocationId) || [];
      existingRoles.push(role);
      stockLocationRoles.set(role.stockLocationId, existingRoles);
      setStockLocationRoles(stockLocationRoles);
      setOpen(false);
      showToast({
        type: ToastType.success,
        title: t(TranslationKey.stockLocationRoleAssignmentCreatedSuccessfully).singular.upper,
      });
    },
  });

  const handleCreate = () => {
    create({
      variables: {
        stockLocationRoleAssignments: [input],
      },
    });
  };

  const footer = (
    <div className='flex items-center justify-end gap-2'>
      <CancelButton onClick={() => setOpen(false)} disabled={loading} />
      <CreateButton onClick={handleCreate} loading={loading} disabled={!input.validate()} />
    </div>
  );

  return (
    <ActionModal
      title={t(TranslationKey.createStockLocationRoleForUserTitle).singular.upper}
      footer={footer}
      open={open}
      setOpen={setOpen}
    >
      <div className='grid gap-4'>
        <DropdownSearchSelect<StockLocation>
          mandatory
          label={t(TranslationKey.stockLocation).singular.upper}
          placeholder={t(TranslationKey.selectStockLocation).singular.upper}
          items={[...stockLocations.values()].filter(sl => !userStockLocationRoles.has(sl.id))}
          textBuilder={item => item.name}
          onSelect={item => setInput(input.withStockLocationId(item?.id))}
          value={stockLocations.get(input.stockLocationId)}
        />
        <DropdownSelect
          label={t(TranslationKey.role).singular.upper}
          items={Object.values(StockLocationRole)}
          onSelect={item => setInput(input.withRole(item))}
          textBuilder={item => StockLocationRoleAssignmentUtil.roleToString(item)}
          value={input.role}
        />
      </div>
    </ActionModal>
  );
}
